import React, { useEffect, useRef, useState } from 'react';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Mousewheel, Keyboard, Autoplay } from 'swiper/modules';
import CardProductFeatured from './CardProductFeatured';
import CardProduct from './Product/CardProduct';
import CardProductGrid from './Product/CardProductGrid';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import StoreService from '../../Services/StoreService';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';
import { useStoreContext } from '../../Contexts/StoreContext';
import { isArraysEqual } from '../../Utils/Helpers';
import { dataFakeForLoading } from '../../Utils/Constants/Product';
import { useTranslation } from 'react-i18next';

import { TbStar } from 'react-icons/tb';

const FeaturedProducts = () => {

    const swiperFeaturedProductsRef = useRef<SwiperRef>(null);
    const { company, isMobile, featuredProducts, setFeaturedProducts, enableLoadProducts, setEnableLoadProducts } = useLayoutContext();

    const [slidesPerView, setSlidesPerView] = useState<number>(() => {
        const storedValue = localStorage.getItem('slides_per_view_featureds');
        return storedValue !== null ? Number(storedValue) : isMobile ? 1.08 : 3;
    });

    const [ loading, setLoading ] = useState<boolean>(featuredProducts && dataFakeForLoading ? isArraysEqual(featuredProducts, dataFakeForLoading) : false);
    const { orderType, getOrderTypeIdByName } = useOrderTypeContext();
    const { currentStore } = useStoreContext();

    const { t, i18n } = useTranslation();
    const currentLang = i18n.language;
    const isFirstRender = useRef(true);

    const loadFeaturedProducts = async (enableLoading:boolean=true) => {
        if (!enableLoadProducts) {
            setEnableLoadProducts(true);
            return;
        }
        setLoading(enableLoading);
        try {
            const storeSevice = new StoreService(orderType);
            const payload = {
                store_id: currentStore.id,
                order_type: Number(getOrderTypeIdByName(orderType))
            }
            const data = await storeSevice.featuredProducts(payload);
            if (data) {
                setFeaturedProducts(data);
            } else {
                setFeaturedProducts([]);
            }
        } catch (error) {
            setFeaturedProducts([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadFeaturedProducts();
        
        const checkSlidesPerView = () => {
            if (window.innerWidth < 640) {
                setSlidesPerView(1.08)
            } else if (window.innerWidth < 768) {
                setSlidesPerView(2.15)
            } else if (window.innerWidth < 1024) {
                setSlidesPerView(3.15)
            } else if (window.innerWidth < 1280) {
                setSlidesPerView(4.15)
            } else {
                setSlidesPerView(5.25)
            }
        }

        checkSlidesPerView()
        window.addEventListener('resize', () => {
            checkSlidesPerView()
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChangeSlide = (index: number) => {
        if (swiperFeaturedProductsRef.current && swiperFeaturedProductsRef.current.swiper) {
            swiperFeaturedProductsRef.current.swiper.slideTo(index);
        }
    }

    useEffect(() => {
        if (currentStore && currentLang && !isFirstRender.current) {
            loadFeaturedProducts(false);
        }
        isFirstRender.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLang]);

    
    useEffect(() => {
        localStorage.setItem('slides_per_view_featureds', slidesPerView.toString());
    }, [slidesPerView]);

    useEffect(() => {
        if (isMobile) {
            localStorage.removeItem('slides_per_view_featureds');
        }
    }, [isMobile]);

    if (!company || isMobile || !featuredProducts || featuredProducts?.length === 0) return null;

    const featuredLayout = String(company?.config?.display_layout?.featured || 'SWIPER_IMAGE').toLowerCase().trim();
    const layout = featuredLayout === 'swiper_image' ? 'SWIPER_IMAGE' : (featuredLayout === 'grid' ? 'GRID' : (featuredLayout === 'list' ? 'LIST' : 'SWIPER_IMAGE'));

    return (
        <div id="category_featured" className='grid self-start w-full gap-4'>
            <h2 className={`flex items-center gap-1 text-xl font-extrabold text-shopfood-title mt-3 ml-2`}>
                <span>{t('layout.featured_products.title')}</span>
                <TbStar />
            </h2>  

            {layout === 'GRID' && (
                <section className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-2 md:gap-3 lg:gap-4 xl:gap-5 px-1 md:px-2">
                    {featuredProducts.map((product: any, index: number) => (
                        <CardProductGrid key={index} product={product} category={product.categories?.[0]} loading={loading} />
                    ))}
                </section>
            )}

            {layout === 'LIST' && (
                <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4 lg:gap-6 px-1 md:px-2">
                    {featuredProducts.map((product: any, index: number) => (
                        <CardProduct key={index} product={product} category={product.categories?.[0]} loading={loading} />
                    ))}
                </section>
            )}

            {(layout !== 'GRID' && layout !== 'LIST') && (
                <Swiper
                    ref={swiperFeaturedProductsRef}
                    spaceBetween={0}
                    modules={[Navigation, Pagination, Mousewheel, Keyboard, Autoplay]}
                    slidesPerView={slidesPerView}
                    centeredSlides={false}
                    keyboard={{enabled: true}}
                    className='grid w-full swiperBullets swiperNavigation'
                    onSlideChange={(swiper: any) => {
                        handleChangeSlide(swiper.activeIndex)
                    }}
                    autoplay={{
                        delay: 3500,
                        disableOnInteraction: true,
                    }}
                    pagination={isMobile && {
                        clickable: true,
                            renderBullet: function (index: number, className: string) {
                            return '<span class="' + className + '"></span>';
                        },
                    }}
                    navigation={!isMobile}
                    >
                    {
                        featuredProducts.map((product: any, index: number) => (
                            <SwiperSlide
                                key={index} data-hash={`slide1${index}`}
                                className={'flex px-1 md:px-2 py-2'}
                                onClick={() => handleChangeSlide(index)}
                                >
                                <CardProductFeatured key={index} product={product} loading={loading} />
                            </SwiperSlide>
                        ))
                    }
                </Swiper>
            )}
        </div>
    )
}

export default FeaturedProducts;
